import { HttpInterceptorFn, HttpContextToken } from "@angular/common/http";
import {
  HTTP_INTERCEPTOR_CONFIG,
  SESSION_CONSTANTS,
} from "@core/constants/auth-constants";
import { environment } from "@environments/environment";
import { API_REGION_PREFIX_MAPPING } from "@core/constants/constants";
import {
  DEFAULT_LANGUAGE_CODE,
  isKnownLanguageCode,
  LANGUAGE_STORAGE_KEY,
} from "@core/constants/languages";

// Context token for region override
export const OVERRIDE_REGION = new HttpContextToken<string | null>(() => null);

function resolveAcceptLanguage(): string {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return stored && isKnownLanguageCode(stored)
    ? stored
    : DEFAULT_LANGUAGE_CODE;
}

/**
 * Request Interceptor
 *
 * This interceptor automatically handles authentication for all HTTP requests
 * by enabling credentials for cookie-based authentication. Cookies are now
 * handled automatically by the browser through HTTP-only cookies set by the backend.
 *
 * Features:
 * - Region-based URL modification for specified endpoints (with configurable exclusions)
 * - Automatic cookie attachment via withCredentials: true
 * - Proper Accept header for JSON responses
 * - Accept-Language from the user's selected language preference
 * - X-Region header addition for region-specific endpoints
 * - Endpoint exclusion for auth endpoints
 */
export const requestInterceptor: HttpInterceptorFn = (request, next) => {
  // Check for URL region modification first
  const needsUrlRegionModification =
    HTTP_INTERCEPTOR_CONFIG.URL_REGION_MODIFICATION.some((endpoint) =>
      request.url.includes(endpoint)
    );

  // Check if this endpoint should be excluded from URL region modification
  const isExcludedFromUrlModification =
    HTTP_INTERCEPTOR_CONFIG.URL_REGION_MODIFICATION_EXCLUDED.some((endpoint) =>
      request.url.includes(endpoint)
    );

  let modifiedRequest = request;

  if (needsUrlRegionModification && !isExcludedFromUrlModification) {
    // Check for region override first
    const regionOverride = request.context.get(OVERRIDE_REGION);

    // Determine which region constant to use based on endpoint type
    let currentRegionId: string | null = null;

    if (regionOverride) {
      // Use the override region if provided
      currentRegionId = regionOverride;
    } else {
      // Check if this is a customer onboarding endpoint
      const isCustomerOnboardingEndpoint =
        HTTP_INTERCEPTOR_CONFIG.CUSTOMER_ONBOARDING_REGION_HEADER_ADDITION.some(
          (endpoint) => request.url.includes(endpoint)
        );

      // Check if this is an audit log endpoint
      const isAuditLogEndpoint =
        HTTP_INTERCEPTOR_CONFIG.AUDIT_LOG_REGION_HEADER_ADDITION.some(
          (endpoint) => request.url.includes(endpoint)
        );

      // Check if this is an analytics endpoint
      const isAnalyticsEndpoint =
        HTTP_INTERCEPTOR_CONFIG.ANALYTICS_REGION_HEADER_ADDITION.some(
          (endpoint) => request.url.includes(endpoint)
        );

      // Use appropriate region constant based on endpoint type
      if (isCustomerOnboardingEndpoint) {
        // For customer onboarding, check if we're in user route
        const isUserRoute =
          typeof globalThis !== "undefined" &&
          (globalThis.location.pathname.includes("/user-management") ||
            globalThis.location.pathname.includes("/user-profile") ||
            globalThis.location.pathname.includes("user"));

        currentRegionId = isUserRoute
          ? sessionStorage.getItem(SESSION_CONSTANTS.X_REGION)
          : sessionStorage.getItem(
              SESSION_CONSTANTS.CUSTOMER_ONBOARDING_REGION
            );
      } else if (isAuditLogEndpoint) {
        currentRegionId = sessionStorage.getItem(
          SESSION_CONSTANTS.AUDIT_LOG_REGION
        );
      } else if (isAnalyticsEndpoint) {
        currentRegionId = sessionStorage.getItem(
          SESSION_CONSTANTS.ANALYTICS_REGION
        );
      } else {
        // Default for user management and other endpoints
        currentRegionId = sessionStorage.getItem(SESSION_CONSTANTS.X_REGION);
      }
    }

    if (currentRegionId) {
      // Find the region prefix mapping with proper type checking
      const regionPrefix =
        API_REGION_PREFIX_MAPPING[
          currentRegionId as keyof typeof API_REGION_PREFIX_MAPPING
        ];

      if (regionPrefix && environment.baseUrl) {
        // Replace the base URL with region-specific URL
        const currentApiUrl = environment.apiUrl;
        const newRegionApiUrl = `${environment.baseUrl}/${regionPrefix}`;

        // Replace the environment.apiUrl with the region-specific URL in the request
        if (request.url.startsWith(currentApiUrl)) {
          const modifiedUrl = request.url.replace(
            currentApiUrl,
            newRegionApiUrl
          );
          modifiedRequest = request.clone({ url: modifiedUrl });
        }
      }
    }
  }


  let headers = modifiedRequest.headers.set("Accept", "application/json");

  // Prefer an explicit Accept-Language (e.g. translation catalog fetch);
  // otherwise use the user's selected language preference.
  if (!headers.has("Accept-Language")) {
    headers = headers.set("Accept-Language", resolveAcceptLanguage());
  }

  // Check for region override first
  const regionOverride = modifiedRequest.context.get(OVERRIDE_REGION);

  // Check if the request needs X-Region header for user management
  const needsXRegionHeader =
    HTTP_INTERCEPTOR_CONFIG.X_REGION_HEADER_ADDITION.some((endpoint) =>
      modifiedRequest.url.includes(endpoint)
    );

  const needsCustomerOnboardingRegionHeader =
    HTTP_INTERCEPTOR_CONFIG.CUSTOMER_ONBOARDING_REGION_HEADER_ADDITION.some(
      (endpoint) => modifiedRequest.url.includes(endpoint)
    );

  if (needsXRegionHeader) {
    const xRegion = sessionStorage.getItem(SESSION_CONSTANTS.X_REGION);
    if (regionOverride) {
      headers = headers.set("X-Region", regionOverride);
    } else if (xRegion) {
      headers = headers.set("X-Region", xRegion);
    }
  }

  // Check if the request needs X-Region header for audit logs
  const needsAuditLogRegionHeader =
    HTTP_INTERCEPTOR_CONFIG.AUDIT_LOG_REGION_HEADER_ADDITION.some((endpoint) =>
      modifiedRequest.url.includes(endpoint)
    );

  if (needsAuditLogRegionHeader) {
    const auditLogRegion = sessionStorage.getItem(
      SESSION_CONSTANTS.AUDIT_LOG_REGION
    );
    if (regionOverride) {
      headers = headers.set("X-Region", regionOverride);
    } else if (auditLogRegion) {
      headers = headers.set("X-Region", auditLogRegion);
    }
  }

  if (needsCustomerOnboardingRegionHeader) {
    // Check if we're in user-related route (user-management, user-profile, etc.)
    const isUserRoute =
      typeof globalThis !== "undefined" &&
      (globalThis.location.pathname.includes("/user-management") ||
        globalThis.location.pathname.includes("/user-profile") ||
        globalThis.location.pathname.includes("user"));

    if (regionOverride) {
      headers = headers.set("X-Region", regionOverride);
    } else if (isUserRoute) {
      // If in user-related route, use X-Region header
      const xRegion = sessionStorage.getItem(SESSION_CONSTANTS.X_REGION);
      if (xRegion) {
        headers = headers.set("X-Region", xRegion);
      }
    } else {
      // Otherwise use customer onboarding region
      const customerOnboardingRegion = sessionStorage.getItem(
        SESSION_CONSTANTS.CUSTOMER_ONBOARDING_REGION
      );
      if (customerOnboardingRegion) {
        headers = headers.set("X-Region", customerOnboardingRegion);
      }
    }
  }

  // Check if the request needs X-Region header for analytics
  const needsAnalyticsRegionHeader =
    HTTP_INTERCEPTOR_CONFIG.ANALYTICS_REGION_HEADER_ADDITION.some((endpoint) =>
      modifiedRequest.url.includes(endpoint)
    );

  if (needsAnalyticsRegionHeader) {
    const analyticsRegion = sessionStorage.getItem(
      SESSION_CONSTANTS.ANALYTICS_REGION
    );
    if (regionOverride) {
      headers = headers.set("X-Region", regionOverride);
    } else if (analyticsRegion) {
      headers = headers.set("X-Region", analyticsRegion);
    }
  }

  const authenticatedRequest = modifiedRequest.clone({
    headers,
    withCredentials: true,
  });

  return next(authenticatedRequest);
};
